import { SelectView } from "./SelectView";
import type { SelectOption, SelectProps } from "./types";

export type { SelectOption, SelectProps } from "./types";

// Radix's shouldShowPlaceholder renders the placeholder for value="", so a real item can
// never be selected via "" — map "" to this sentinel to keep it a real, selectable item.
const EMPTY_VALUE_SENTINEL = "__select-empty-value__";

const logInvalidOptions = (options: readonly SelectOption[]) => {
  if (!import.meta.env.DEV) return;

  const invalidLabels = options
    .filter((option) => option.value === "" || option.value === EMPTY_VALUE_SENTINEL)
    .map((option) => option.label);
  if (invalidLabels.length > 0) {
    console.error(
      `Select: option value must be non-empty and not equal to the reserved sentinel "${EMPTY_VALUE_SENTINEL}". Dropping offending option(s): ${invalidLabels.join(", ")}`
    );
  }

  const seen = new Set<string>();
  const duplicateLabels = options
    .filter((option) => {
      if (seen.has(option.value)) return true;
      seen.add(option.value);
      return false;
    })
    .map((option) => option.label);
  if (duplicateLabels.length > 0) {
    console.error(
      `Select: option value must be non-empty and unique. Duplicate value(s) found for: ${duplicateLabels.join(", ")}`
    );
  }
};

export const Select = ({
  value,
  onChange,
  options,
  placeholder,
  id,
  disabled = false,
  className,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
}: SelectProps) => {
  logInvalidOptions(options);

  const validOptions = options.filter(
    (option, index, all) =>
      option.value !== "" &&
      option.value !== EMPTY_VALUE_SENTINEL &&
      all.findIndex((candidate) => candidate.value === option.value) === index
  );
  const hasMatchingOption = validOptions.some((option) => option.value === value);
  const isUnresolvedValue = value !== "" && !hasMatchingOption;

  const radixOptions: SelectOption[] = [
    { value: EMPTY_VALUE_SENTINEL, label: placeholder },
    // A value can arrive before its options load, or point at a deleted record — render it
    // as a real, distinct item so the trigger shows "set but unresolvable", not "empty".
    ...(isUnresolvedValue ? [{ value, label: "Unavailable" }] : []),
    ...validOptions,
  ];

  const handleValueChange = (nextValue: string) => {
    onChange(nextValue === EMPTY_VALUE_SENTINEL ? "" : nextValue);
  };

  return (
    <SelectView
      value={value === "" ? EMPTY_VALUE_SENTINEL : value}
      options={radixOptions}
      id={id}
      disabled={disabled}
      className={className}
      ariaLabel={ariaLabel}
      ariaDescribedBy={ariaDescribedBy}
      ariaInvalid={ariaInvalid}
      onValueChange={handleValueChange}
    />
  );
};
